import type { Locale } from "../i18n";
import { intlLocale } from "../i18n";

/** "just now", "5 min ago", "3 h ago", "2 d ago" — in the viewer's language. */
export function relativeTime(value: string | null, locale: Locale, now = Date.now()): string {
  if (!value) return "—";
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "—";
  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  const fr = locale === "fr";
  if (seconds < 60) return fr ? "à l'instant" : "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return fr ? `il y a ${minutes} min` : `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return fr ? `il y a ${hours} h` : `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return fr ? `il y a ${days} j` : `${days} d ago`;
}

/** "in 5 h" / "dans 5 h" for a moment still ahead. Past moments read "now". */
export function untilTime(value: string, locale: Locale, now = Date.now()): string {
  const seconds = Math.floor((new Date(value).getTime() - now) / 1000);
  const fr = locale === "fr";
  if (seconds <= 60) return fr ? "maintenant" : "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return fr ? `dans ${minutes} min` : `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return fr ? `dans ${hours} h` : `in ${hours} h`;
  const days = Math.floor(hours / 24);
  return fr ? `dans ${days} j` : `in ${days} d`;
}

export function formatDateTime(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(intlLocale(locale), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(date);
}

export function formatDay(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(intlLocale(locale), { day: "numeric", month: "short", year: "numeric" }).format(date);
}

/** `2026-09` → "September 2026" / "septembre 2026". */
export function formatMonth(month: string, locale: Locale): string {
  const [year, number] = month.split("-").map(Number);
  return new Intl.DateTimeFormat(intlLocale(locale), { month: "long", year: "numeric", timeZone: "UTC" })
    .format(new Date(Date.UTC(year ?? 1970, (number ?? 1) - 1, 1)));
}

/** An executive figure: cents are enough. Exact accounting uses formatMoney. */
export function formatSpend(microUsd: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })
    .format(microUsd / 1_000_000);
}

export function formatMoney(microUsd: number): string {
  const digits = microUsd < 1_000_000 ? 4 : 2;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits })
    .format(microUsd / 1_000_000);
}

/** A last-resort label for an unknown internal code: `start_migration` → "Start migration". */
export function humanize(value: string): string {
  const words = value.replaceAll(/[._-]+/g, " ").trim().split(" ");
  const acronyms = new Set(["api", "dns", "geo", "seo", "tls", "url"]);
  return words.map((word, index) => {
    if (acronyms.has(word.toLowerCase())) return word.toUpperCase();
    return index === 0 ? word.replace(/^./, (character) => character.toUpperCase()) : word;
  }).join(" ");
}

export async function operatorFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, init);
  const body = (await response.json().catch(() => ({}))) as T & { error?: { message?: string; code?: string } };
  if (!response.ok) {
    const error = new Error(body.error?.message ?? `Request failed (${response.status})`) as Error & { code?: string };
    if (body.error?.code) error.code = body.error.code;
    throw error;
  }
  return body;
}

/** A POST with a fresh idempotency key, so a double click is one decision. */
export function operatorPost<T>(path: string, body: unknown = {}): Promise<T> {
  return operatorFetch<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify(body),
  });
}
